import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { ActivQMSLoader } from "../../components/ui/activqms-loader";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { COMPLETE_LINKEDIN_OAUTH } from "../../services/linkedin-api";

interface OAuthResult {
  success: boolean;
  adAccountName?: string;
  error?: string;
}

export function LinkedInOAuthCallback() {
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);
  const [result, setResult] = useState<OAuthResult | null>(null);

  const [completeOAuth] = useMutation(COMPLETE_LINKEDIN_OAUTH, {
    onCompleted: (data) => {
      const oauthResult = data.completeLinkedInOAuth;
      setResult(oauthResult);
      setIsProcessing(false);

      // Send message to parent window
      if (window.opener) {
        if (oauthResult.success) {
          window.opener.postMessage(
            {
              type: "LINKEDIN_OAUTH_SUCCESS",
              adAccountName: oauthResult.adAccountName,
            },
            window.location.origin
          );
        } else {
          window.opener.postMessage(
            {
              type: "LINKEDIN_OAUTH_ERROR",
              error: oauthResult.error,
            },
            window.location.origin
          );
        }

        // Close popup after a short delay
        setTimeout(() => {
          window.close();
        }, 2000);
      }
    },
    onError: (error) => {
      console.error("OAuth completion error:", error);
      setResult({
        success: false,
        error: "Failed to complete OAuth process",
      });
      setIsProcessing(false);

      // Send error message to parent window
      if (window.opener) {
        window.opener.postMessage(
          {
            type: "LINKEDIN_OAUTH_ERROR",
            error: error.message,
          },
          window.location.origin
        );

        // Close popup after a short delay
        setTimeout(() => {
          window.close();
        }, 2000);
      }
    },
  });

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const error = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");

    // Handle OAuth errors
    if (error) {
      setResult({
        success: false,
        error: errorDescription || error,
      });
      setIsProcessing(false);

      if (window.opener) {
        window.opener.postMessage(
          {
            type: "LINKEDIN_OAUTH_ERROR",
            error: errorDescription || error,
          },
          window.location.origin
        );

        setTimeout(() => {
          window.close();
        }, 2000);
      }
      return;
    }

    // Handle successful OAuth callback
    if (code && state) {
      // Complete the OAuth process
      completeOAuth({
        variables: {
          input: {
            code,
            state,
          },
        },
      });
    } else {
      setResult({
        success: false,
        error: "Missing OAuth parameters",
      });
      setIsProcessing(false);

      if (window.opener) {
        window.opener.postMessage(
          {
            type: "LINKEDIN_OAUTH_ERROR",
            error: "Missing OAuth parameters",
          },
          window.location.origin
        );

        setTimeout(() => {
          window.close();
        }, 2000);
      }
    }
  }, [searchParams, completeOAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="text-center">
          {/* LinkedIn Logo */}
          <div className="flex justify-center mb-6">
            <svg
              className="h-16 w-16 text-[#0A66C2]"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
          </div>

          {isProcessing ? (
            <>
              <ActivQMSLoader size="lg" />
              <h2 className="mt-6 text-2xl font-bold text-gray-900">
                Completing LinkedIn Connection
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Please wait while we complete the connection...
              </p>
            </>
          ) : result?.success ? (
            <>
              <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500" />
              <h2 className="mt-6 text-2xl font-bold text-gray-900">
                Successfully Connected!
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Your LinkedIn Ads account has been connected to activQMS.
              </p>
              {result.adAccountName && (
                <p className="mt-2 text-sm font-medium text-gray-900">
                  Account: {result.adAccountName}
                </p>
              )}
              <p className="mt-4 text-xs text-gray-500">
                This window will close automatically...
              </p>
            </>
          ) : (
            <>
              <XCircleIcon className="mx-auto h-16 w-16 text-red-500" />
              <h2 className="mt-6 text-2xl font-bold text-gray-900">
                Connection Failed
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {result?.error || "An error occurred during the connection process."}
              </p>
              <p className="mt-4 text-xs text-gray-500">
                This window will close automatically...
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

